import React from 'react';
import { FormattedMessage } from 'react-intl';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import ExpandableCard from '../../../commons/ExpandableCard';
import { AssociateRequiredReviews, FilterValues } from '../types/types';
import UserBadge from '../../Dashboard/DashboardComponents/UserBadge';
import UserBadgeThumbnails from '../../Dashboard/DashboardComponents/UserBadgeThumbnails';
import useInspireOptions from '../../../hooks/useInspireOptions';

interface AssociatesRequiredReviewsCardProps {
  associatesWithRequiredReviews: AssociateRequiredReviews[];
  onFilterChange: (newFilters: Partial<FilterValues>) => void;
}

const AssociatesRequiredReviewsCard: React.FC<
  AssociatesRequiredReviewsCardProps
> = ({ associatesWithRequiredReviews, onFilterChange }) => {
  const { isTm, isOm, requesterUserID, requesterUsername } =
    useInspireOptions();

  const title = (
    <FormattedMessage
      id='cs_perfportal_associates_with_remaining_required_outliers'
      defaultMessage='Associates with remaining reviews required'
    />
  );

  const header = (
    <Row
      spacingInset='500 200 200 none'
      alignmentVertical='center'
      width='fill'
    >
      <Row spacing='300' alignmentVertical='center'>
        <Text type='h400'>{associatesWithRequiredReviews.length}</Text>
        <Text type='h50'>
          <FormattedMessage
            id='cs_perfportal_associates_remaining'
            defaultMessage='Associates remaining'
          />
        </Text>
        <UserBadgeThumbnails
          usernames={associatesWithRequiredReviews
            .slice(0, 5)
            .map(associate => associate.login)}
          maxThumbnailNumber={5}
        />
      </Row>
    </Row>
  );

  const expandedContent = (
    <Column spacing='small' width='fill'>
      <Row
        spacingInset='300 none 200 none'
        width='fill'
        alignmentHorizontal='justify'
      >
        <Text type='b200' color='secondary'>
          <FormattedMessage
            id='cs_perfportal_table_associate'
            defaultMessage='Associate'
          />
        </Text>
        <Row width='200px' alignmentHorizontal='end'>
          <Text type='b200' color='secondary'>
            <FormattedMessage
              id='cs_perfportal_outlier_remaining_reviews_required'
              defaultMessage='Remaining reviews required'
            />
          </Text>
        </Row>
      </Row>

      <Column spacing='300' width='fill'>
        {associatesWithRequiredReviews.map(associate => (
          <Row
            key={associate.login}
            width='fill'
            alignmentHorizontal='justify'
            alignmentVertical='center'
          >
            <Row alignmentVertical='center' spacing='300'>
              <UserBadge
                username={associate.login}
                thumbNail
                thumbnailSize='small'
              />
              <Text>{associate.login}</Text>
            </Row>
            <Row
              width='200px'
              alignmentHorizontal='end'
              alignmentVertical='center'
              spacing='100'
            >
              <Text type='h400'>
                {associate.requiredReviews.remainingRequired}
              </Text>
              <Text type='b200' color='secondary'>
                / {associate.requiredReviews.totalRequired}
              </Text>
            </Row>
          </Row>
        ))}
      </Column>
    </Column>
  );

  return (
    <ExpandableCard
      title={title}
      header={header}
      expandedContent={expandedContent}
      cardSpacingInset='450 400 400 450'
      clickstreamTags={{
        'data-csa-c-type': 'link',
        'data-csa-c-slot-id': `tm-outlier-management-associate-required-reviews-link-slot`,
        'data-csa-c-tag1': 'Coaching',
        'data-csa-c-tag2': 'Outlier Metrics',
        'data-csa-c-tag3': `Associates Required Reviews`,
        'data-csa-c-profile-id': requesterUserID,
        'data-csa-c-username': requesterUsername,
        'data-csa-c-is-TM': isTm,
        'data-csa-c-is-OM': isOm,
      }}
    />
  );
};

export default AssociatesRequiredReviewsCard;
